import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const initialForm = {
  name_of_organisation: '',
  owner_of_organisation: '',
  place: '',
  district: '',
  location_map: '',
  years_of_experience: '',
  contact_number: '',
  whatsapp_number: '',
  email_id: '',
  website: '',
  organisation_license: null,
  organisation_photo: null,
  organisation_name_board_photo: null
};

const partnershipPoints = [
  'Grow together with Dani Job Club as a regional recruitment partner.',
  'Collect registrations, share candidate databases, and let Dani job-match your members.',
  'Earn through a clear partnership commission model while expanding your local trust.'
];

const highlightCards = [
  {
    label: 'Partner Role',
    title: 'Act as a trusted local consultancy partner',
    copy: 'You can onboard candidates, collect the registration fee, and send verified databases to Dani Job Club.'
  },
  {
    label: 'How It Works',
    title: 'Dani finds jobs for your registered members',
    copy: 'The program is designed for growth on both sides with a structured partnership workflow and commission support.'
  },
  {
    label: 'Ready To Start',
    title: 'Submit your organisation details here',
    copy: 'Once saved, your partnership request will appear inside the admin dashboard for review and follow-up.'
  }
];

const fileLabelClassName = 'block rounded-[24px] border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-sm font-semibold text-gray-700';
const inputClassName = 'mt-2 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200';

const Partnership = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contact_number' || name === 'whatsapp_number') {
      setForm((prev) => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 13) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files && files[0] ? files[0] : null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('name_of_organisation', form.name_of_organisation);
      formData.append('owner_of_organisation', form.owner_of_organisation);
      formData.append('place', form.place);
      formData.append('district', form.district);
      formData.append('location_map', form.location_map);
      formData.append('years_of_experience', form.years_of_experience);
      formData.append('contact_number', form.contact_number);
      formData.append('whatsapp_number', form.whatsapp_number);
      formData.append('email_id', form.email_id);
      formData.append('website', form.website);
      if (form.organisation_license) formData.append('organisation_license', form.organisation_license);
      if (form.organisation_photo) formData.append('organisation_photo', form.organisation_photo);
      if (form.organisation_name_board_photo) {
        formData.append('organisation_name_board_photo', form.organisation_name_board_photo);
      }

      const res = await axios.post(`${API_URL}/api/partnerships`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(res.data?.message || 'Partnership request submitted successfully.');
      setForm(initialForm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit partnership request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.22),transparent_16%),linear-gradient(180deg,#fffdf7_0%,#fffaf0_42%,#ffffff_100%)] py-8 text-black">
      <div className="mx-auto max-w-7xl px-4 md:px-8 space-y-10">
        <section className="relative overflow-hidden rounded-3xl border border-yellow-300 bg-[linear-gradient(135deg,#050505_0%,#171717_46%,#b68900_100%)] px-4 py-8 md:px-10 md:py-12 text-white shadow-lg">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,214,10,0.24),transparent_24%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.10),transparent_24%)]" />
          <div className="relative grid grid-cols-1 gap-8 xl:grid-cols-2 xl:items-end">
            <div>
              <p className="text-sm font-extrabold uppercase tracking-[0.34em] text-yellow-300">Partnership Program</p>
              <h1 className="mt-4 max-w-4xl text-4xl font-extrabold leading-tight md:text-5xl xl:text-6xl">
                Build a consultancy partnership with Dani Job Club and grow together.
              </h1>

              <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
                {partnershipPoints.map((point) => (
                  <div key={point} className="rounded-[24px] border border-white/15 bg-white/10 px-4 py-4 text-sm font-semibold leading-6 text-white/90 backdrop-blur-sm">
                    {point}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4">
              {highlightCards.map((card) => (
                <div key={card.label} className="rounded-[28px] border border-white/15 bg-white/10 px-5 py-5 backdrop-blur-sm">
                  <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-yellow-300">{card.label}</p>
                  <h2 className="mt-3 text-2xl font-extrabold leading-tight">{card.title}</h2>
                  <p className="mt-2 text-sm leading-7 text-white/82">{card.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          {/* Partnership Form restored below */}
          <div className="max-w-2xl mx-auto mb-12">
            <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-3xl shadow-lg p-8 border border-yellow-200">
              <h2 className="text-2xl font-bold mb-2 text-yellow-700">Partnership Request Form</h2>
              {error && <div className="mb-4 rounded bg-red-100 px-4 py-2 text-red-700">{error}</div>}
              {success && <div className="mb-4 rounded bg-green-100 px-4 py-2 text-green-700">{success}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-semibold">Name of Organisation</label>
                  <input className={inputClassName} name="name_of_organisation" value={form.name_of_organisation} onChange={handleChange} required />
                </div>
                <div>
                  <label className="font-semibold">Owner of Organisation</label>
                  <input className={inputClassName} name="owner_of_organisation" value={form.owner_of_organisation} onChange={handleChange} required />
                </div>
                <div>
                  <label className="font-semibold">Place</label>
                  <input className={inputClassName} name="place" value={form.place} onChange={handleChange} required />
                </div>
                <div>
                  <label className="font-semibold">District</label>
                  <input className={inputClassName} name="district" value={form.district} onChange={handleChange} required />
                </div>
                <div>
                  <label className="font-semibold">Location Map (Google Maps link)</label>
                  <input className={inputClassName} name="location_map" value={form.location_map} onChange={handleChange} />
                </div>
                <div>
                  <label className="font-semibold">Years of Experience</label>
                  <input className={inputClassName} name="years_of_experience" value={form.years_of_experience} onChange={handleChange} />
                </div>
                <div>
                  <label className="font-semibold">Contact Number</label>
                  <input className={inputClassName} name="contact_number" value={form.contact_number} onChange={handleChange} required maxLength={13} />
                </div>
                <div>
                  <label className="font-semibold">WhatsApp Number</label>
                  <input className={inputClassName} name="whatsapp_number" value={form.whatsapp_number} onChange={handleChange} maxLength={13} />
                </div>
                <div>
                  <label className="font-semibold">Email ID</label>
                  <input className={inputClassName} name="email_id" value={form.email_id} onChange={handleChange} type="email" required />
                </div>
                <div>
                  <label className="font-semibold">Website</label>
                  <input className={inputClassName} name="website" value={form.website} onChange={handleChange} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={fileLabelClassName}>
                    Organisation License (PDF/JPG)
                    <input type="file" name="organisation_license" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileChange} />
                  </label>
                </div>
                <div>
                  <label className={fileLabelClassName}>
                    Organisation Photo
                    <input type="file" name="organisation_photo" accept=".jpg,.jpeg,.png" className="hidden" onChange={handleFileChange} />
                  </label>
                </div>
                <div>
                  <label className={fileLabelClassName}>
                    Name Board Photo
                    <input type="file" name="organisation_name_board_photo" accept=".jpg,.jpeg,.png" className="hidden" onChange={handleFileChange} />
                  </label>
                </div>
              </div>
              <button type="submit" className="mt-6 w-full rounded-2xl bg-yellow-500 px-6 py-3 font-bold text-white shadow hover:bg-yellow-600 transition" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Partnership Request'}
              </button>
            </form>
          </div>
          {/* Member registrations table - adjust columns for single view, no horizontal scroll */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Place</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">District</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qualification</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Map your member registrations here. Example: */}
                {/* registrations.map((reg) => (
                  <tr key={reg.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.contact_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.whatsapp_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.age}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.gender}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.place}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.district}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.qualification}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.experience_level}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{reg.expecting_job}</td>
                  </tr>
                )) */}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Partnership;
